import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, RawButton } from '@common-fe/common-fe';
import { toNumber } from 'lodash';
import styled from 'styled-components';

import { DropButton } from '@/components/controls/DropButton';
import { PaymentMethod } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { PaymentMethodType } from '@/modules/user/user.types';

interface StyledButtonProps {
  isWarning?: boolean;
}

const StyledButton = styled(RawButton)<StyledButtonProps>`
  height: 32px;
  min-height: 32px;
  ${({ theme }) => theme.fonts.text14Regular};
  border-radius: ${({ theme }) => theme.rounds.button2Round};
  color: ${({ theme, isWarning }) => (isWarning ? theme.colors.danger : theme.colors.textBody)};
  padding: ${({ theme }) => `${theme.spacings.spacing4} ${theme.spacings.spacing8}`};

  &:hover {
    color: ${({ theme, isWarning }) => (isWarning ? theme.colors.danger : theme.colors.textAccent)};
    background-color: ${({ theme }) => theme.colors.accentContainer};
  }
`;

interface Props {
  method: PaymentMethod;
  handleSetEditableMethodPaymentMethod: (method: PaymentMethod) => void;
  removePaymentMethod?: () => void;
  editPaymentMethod?: () => void;
  updateDefaultPaymentMethod?: (paymentMethodId: number) => void;
}

const PaymentMethodOptions: React.FC<Props> = ({
  method,
  handleSetEditableMethodPaymentMethod,
  removePaymentMethod,
  editPaymentMethod,
  updateDefaultPaymentMethod,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const hasEditButton = useMemo(
    () =>
      method.type === PaymentMethodType.PAYPAL ||
      method.type === PaymentMethodType.VENMO,
    [method.type]
  );

  const hasRemoveButton = useMemo(
    () =>
      method.type === PaymentMethodType.DIRECT_DEPOSIT ||
      method.type === PaymentMethodType.DEBIT ||
      method.type === PaymentMethodType.PAYPAL ||
      method.type === PaymentMethodType.VENMO,
    [method.type]
  );

  const handleEdit = useCallback(() => {
    if (editPaymentMethod) {
      handleSetEditableMethodPaymentMethod(method);
      editPaymentMethod();
    }
    setIsExpanded(false);
  }, [method, editPaymentMethod, handleSetEditableMethodPaymentMethod]);

  const handleRemove = useCallback(() => {
    if (removePaymentMethod) {
      handleSetEditableMethodPaymentMethod(method);
      removePaymentMethod();
    }
    setIsExpanded(false);
  }, [method, removePaymentMethod, handleSetEditableMethodPaymentMethod]);

  const handleUpdateDefaultPaymentMethod = useCallback(() => {
    updateDefaultPaymentMethod?.(toNumber(method?.id));
    setIsExpanded(false);
  }, [method?.id, updateDefaultPaymentMethod]);

  return (
    <DropButton
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      contentStyle={{
        top: 'auto',
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
      pad="spacing12"
      height={{ min: '45px' }}
    >
      <Box gap="spacing2">
        {hasEditButton ? (
          <StyledButton
            data-testId="edit-payment-method_button"
            type="button"
            onClick={handleEdit}
          >
            {t('Edit')}
          </StyledButton>
        ) : null}

        <StyledButton
          data-testId="set-as-default-payment-method_button"
          type="button"
          onClick={handleUpdateDefaultPaymentMethod}
        >
          {t('Set as default')}
        </StyledButton>

        {hasRemoveButton ? (
          <StyledButton
            data-testId="remove-payment-method_button"
            type="button"
            isWarning
            onClick={handleRemove}
          >
            {t('Remove')}
          </StyledButton>
        ) : null}
      </Box>
    </DropButton>
  );
};

export default PaymentMethodOptions;
